import { Injectable, signal, inject } from '@angular/core';
import { ProfessionalsWizardService } from './professionals-wizard.service';
import { RegistrationStep } from '../../core/services/registration-step.service';

export type TipologiaFiduciario =
  | 'AUTO_EXPERT'
  | 'RE_EXPERT'
  | 'INVESTIGATOR'
  | 'DOCTOR'
  | 'LAWYER'
  | 'BODYSHOP'
  | 'NK_BODYSHOP'
  | 'HAIL_PROVIDER'
  | 'NETWORK';

export type TipologiaCarrozzeria = 'STANDARD' | 'ARS' | 'RCI' | 'NOBIS' | 'AXA_DEUTSCHE_BANK';

export interface ProfessionalsData {
  tipologiaFiduciario: TipologiaFiduciario | null;
  tipologiaCarrozzeria: TipologiaCarrozzeria | null;
  generalData: any; // Will be populated from professionals-general-data
  specificData: any; // Will be populated from professionals-specific-data
  attachmentsData: any; // Will be populated from professionals-questionnaire-attachments
}

@Injectable({
  providedIn: 'root',
})
export class ProfessionalsDataService {
  private professionalsWizardService = inject(ProfessionalsWizardService);
  
  // Store current professionals session data
  private professionalsData = signal<ProfessionalsData>({
    tipologiaFiduciario: null,
    tipologiaCarrozzeria: null,
    generalData: null,
    specificData: null,
    attachmentsData: null,
  });

  /**
   * Initialize professionals data from user session/login
   */
  initializeProfessionalsData(
    tipologia: TipologiaFiduciario,
    tipologiaCarrozzeria?: TipologiaCarrozzeria,
  ) {
    this.professionalsData.update((data) => ({
      ...data,
      tipologiaFiduciario: tipologia,
      tipologiaCarrozzeria: tipologiaCarrozzeria || null,
    }));
  }

  /**
   * Get current tipologia fiduciario
   */
  getTipologiaFiduciario(): TipologiaFiduciario | null {
    return this.professionalsData().tipologiaFiduciario;
  }

  /**
   * Get current tipologia carrozzeria
   */
  getTipologiaCarrozzeria(): TipologiaCarrozzeria | null {
    return this.professionalsData().tipologiaCarrozzeria;
  }

  /**
   * Save general data (step 1)
   */
  saveGeneralData(data: any) {
    this.professionalsData.update((current) => ({
      ...current,
      generalData: data,
    }));
  }

  /**
   * Save specific data (step 2)
   */
  saveSpecificData(data: any) {
    this.professionalsData.update((current) => ({
      ...current,
      specificData: data,
    }));
  }

  /**
   * Save attachments data (step 3)
   */
  saveAttachmentsData(data: any) {
    this.professionalsData.update((current) => ({
      ...current,
      attachmentsData: data,
    }));
  }

  /**
   * Get all professionals data for API submission
   */
  getAllData(): ProfessionalsData {
    return this.professionalsData();
  }

  /**
   * Clear all data
   */
  clearData() {
    this.professionalsData.set({
      tipologiaFiduciario: null,
      tipologiaCarrozzeria: null,
      generalData: null,
      specificData: null,
      attachmentsData: null,
    });
  }

  /**
   * Check if wizard pages should be in read-only mode
   * Returns true if current registration step is VERIFICATION or beyond
   * This automatically refreshes based on backend state, solving refresh issues
   */
  isReadOnly(): boolean {
    // NK_BODYSHOP onboarding (backoffice_professional entering data for a network body shop)
    // is always editable regardless of any stale registration step value in the wizard service.
    // This stale value comes from review-professional loading another professional's step before
    // navigating to the NK- route, causing false read-only on first SPA navigation.
    if (this.professionalsWizardService.isNetworkOnboarding()) {
      return false;
    }

    const currentStep = this.professionalsWizardService.currentRegistrationStep();
    
    // If no step loaded yet, not read-only
    if (!currentStep) {
      return false;
    }
    
    // Read-only una volta inviata l'anagrafica: VERIFICATION, MFA_SETUP e le fasi contratto.
    // In MFA_SETUP l'anagrafica è già stata inviata e validata: il fiduciario può solo
    // visualizzarla (mai modificarla) mentre compila/attende la validazione dei dati MFA.
    return currentStep === RegistrationStep.VERIFICATION ||
           currentStep === RegistrationStep.MFA_SETUP ||
           currentStep === RegistrationStep.CONTRACT_SENT ||
           currentStep === RegistrationStep.CONTRACT_SIGNATURE ||
           currentStep === RegistrationStep.CONTRACT_COUNTERSIGNATURE;
  }
}
